import React from 'react';
import { Room } from '../../../types/accommodation';
import { RoomTableRow } from './RoomTableRow';
import { useLanguage } from '../../../contexts/LanguageContext';
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';

type Props = {
  rooms: Room[];
  onViewRoom: (roomNumber: number) => void;
  onAddRoom?: () => void;
  onRemoveRoom?: (roomNumber: number) => Promise<void>;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  startIndex: number;
  totalRooms: number;
  itemsPerPage: number;
  onItemsPerPageChange: (value: number) => void;
};

export const RoomTable = ({ 
  rooms, 
  onViewRoom,
  onAddRoom,
  onRemoveRoom,
  currentPage,
  totalPages,
  onPageChange,
  startIndex,
  totalRooms,
  itemsPerPage,
  onItemsPerPageChange
}: Props) => {
  const { translations, direction } = useLanguage();
  const userRole = localStorage.getItem('userRole');

  // Sort rooms by room number
  const sortedRooms = [...rooms].sort((a, b) => a.n - b.n);

  return (
    <div className="w-full">
      {/* Add Room Button - Only visible for admin */}
      {userRole === 'admin' && onAddRoom && (
        <div className="p-4 flex justify-end">
          <button
            onClick={onAddRoom}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <Plus size={20} />
            <span>{translations?.roomTable?.addRoom || 'Add Room'}</span>
          </button>
        </div>
      )}

      {/* Mobile View */}
      <div className="md:hidden">
        {sortedRooms.map((room) => (
          <div
            key={room.n}
            className="bg-white p-4 border-b last:border-b-0 hover:bg-gray-50"
          >
            {/* Room Number and Type */}
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-semibold text-lg">
                  {translations?.roomTable?.headers?.roomNumber || 'Room'}{' '}
                  {room.n}
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 mt-1">
                  {room.t === 'd'
                    ? translations?.roomTable?.headers?.double || 'Double'
                    : room.t === 't'
                    ? translations?.roomTable?.headers?.triple || 'Triple'
                    : translations?.roomTable?.headers?.quadruple || 'Quadruple'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {userRole === 'admin' && onRemoveRoom && room.o === 0 && (
                  <button
                    onClick={() => onRemoveRoom(room.n)}
                    className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <Trash2 size={16} />
                    <span>{translations?.roomTable?.headers?.remove || 'Remove'}</span>
                  </button>
                )}
                <button
                  onClick={() => onViewRoom(room.n)}
                  className="px-3 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  {translations?.roomTable?.headers?.actions || 'View'}
                </button>
              </div>
            </div>

            {/* Capacity and Occupancy */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">
                  {translations?.roomTable?.headers?.capacity || 'Capacity'}
                </span>
                <span className="font-medium">{room.c}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">
                  {translations?.roomTable?.headers?.occupancy || 'Occupied'}
                </span>
                <span className="font-medium">{room.o}</span>
              </div>
              <div className="relative pt-1">
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      room.o >= room.c
                        ? 'bg-red-500'
                        : room.o > 0
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{
                      width: `${Math.min((room.o / room.c) * 100, 100)}%`
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                  direction === 'ltr' ? 'text-left' : 'text-right'
                }`}
              >
                {translations?.roomTable?.headers?.roomNumber || 'Room Number'}
              </th>
              <th
                className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                  direction === 'ltr' ? 'text-left' : 'text-right'
                }`}
              >
                {translations?.roomTable?.headers?.roomType || 'Room Type'}
              </th>
              <th
                className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                  direction === 'ltr' ? 'text-left' : 'text-right'
                }`}
              >
                {translations?.roomTable?.headers?.capacity || 'Capacity'}
              </th>
              <th
                className={`px-6 py-3 text-sm font-medium text-gray-500 ${
                  direction === 'ltr' ? 'text-left' : 'text-right'
                }`}
              >
                {translations?.roomTable?.headers?.occupancy || 'Occupancy'}
              </th>
            
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedRooms.map((room) => (
              <RoomTableRow
                key={room.n}
                room={room}
                onViewRoom={onViewRoom}
                onRemoveRoom={onRemoveRoom}
                canRemove={userRole === 'admin' && room.o === 0}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination and Items Per Page */}
      <div className="bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t">
        {/* Items Per Page Selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">
            {translations?.pagination?.itemsPerPage || 'Items per page:'}
          </label>
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="px-2 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value={10}>10</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-between sm:justify-end flex-1 gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`p-1 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRight size={20} />
            </button>

            <span className="text-sm text-gray-700">
              {translations?.pagination?.page || 'Page'} {currentPage}{' '}
              {translations?.pagination?.of || 'of'} {totalPages}
            </span>

            <button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`p-1 rounded-lg ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          <div className="text-sm text-gray-500">
            {translations?.pagination?.showing || 'Showing'} {startIndex + 1}-{Math.min(startIndex + rooms.length, totalRooms)} {translations?.pagination?.of || 'of'} {totalRooms}
          </div>
        </div>
      </div>
    </div>
  );
};